import { Mesh } from "../basePass";
import { GStack, GetVec3, Matrix4x4, Vector3 } from "../core/types";
import { GLTF_ELEMENTS_PER_TYPE, GltfLoader } from "./glTFLoader/gltf-loader";
import { Vec3MulMatrix } from "../core/utils";
import { GSDFMeshGenerator } from "../sdfMesh";

const DEBUG_LOG = false;
const DEBUG_LOG_ATTRIBUTES = false && DEBUG_LOG;

export class GMeshLoader {
    static async Load(
        gl: WebGL2RenderingContext,
        pMesh: Mesh,
        path: string,
        meshIndex: number,
        preTransformMat: Matrix4x4 | null,
        bGenerateSDFTexture: boolean,
    ) {
        const loader = new GltfLoader();
        try {
            const asset = await loader.load(path);
            const gltf = asset.gltf;
            if (DEBUG_LOG) {
                console.log(path);
                console.log(gltf);
            }

            const curMesh = gltf.meshes![meshIndex];
            const curPrimitive = curMesh.primitives![0];

            let IndexBufferCPU;
            let VertexBufferCPU;

            //Get Indices
            if (curPrimitive.indices !== undefined) {
                const indexBufferAcc = asset.gltf.accessors![curPrimitive.indices];
                if (indexBufferAcc.componentType != WebGL2RenderingContext.UNSIGNED_SHORT) {
                    //console.log("Changed Index Buffer Element Type");
                    pMesh.IndexBufferElementType = indexBufferAcc.componentType;
                }
                pMesh.NumIndices = indexBufferAcc.count;
                IndexBufferCPU = await asset.LoadAccessor(curPrimitive.indices);
                pMesh.IndexBufferGPU = gl.createBuffer();
                gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, pMesh.IndexBufferGPU);
                gl.bufferData(gl.ELEMENT_ARRAY_BUFFER, IndexBufferCPU, gl.STATIC_DRAW);
                if (DEBUG_LOG_ATTRIBUTES) {
                    //console.log(`NUM INDICES :` + indexBufferAcc.count);
                }
            }

            // POSITION | NORMAL | TANGENT | TEXCOORD_n

            //Get Positions
            {
                VertexBufferCPU = await asset.LoadAccessor(curPrimitive.attributes.POSITION);
                const curAccessor = asset.gltf.accessors![curPrimitive.attributes.POSITION];
                pMesh.NumVertices = curAccessor.count;

                if (curAccessor.min !== undefined) {
                    pMesh.InitPositionMin = new Vector3(curAccessor.min[0], curAccessor.min[1], curAccessor.min[2]);
                    if (DEBUG_LOG_ATTRIBUTES) {
                        console.log(
                            `MeshMin : ${pMesh.InitPositionMin.x}, ${pMesh.InitPositionMin.y}, ${pMesh.InitPositionMin.z}`,
                        );
                    }
                } else {
                    console.error("NO MIN MAX ON OCCLUDER PLANE");
                }
                if (curAccessor.max !== undefined) {
                    pMesh.InitPositionMax = new Vector3(curAccessor.max[0], curAccessor.max[1], curAccessor.max[2]);
                    if (DEBUG_LOG_ATTRIBUTES) {
                        console.log(
                            `MeshMax : ${pMesh.InitPositionMax.x}, ${pMesh.InitPositionMax.y}, ${pMesh.InitPositionMax.z}`,
                        );
                    }
                } else {
                    console.error("NO MIN MAX ON OCCLUDER PLANE");
                }

                if (pMesh.InitPositionMin && pMesh.InitPositionMax) {
                    //compute BBox
                    pMesh.InitBBoxCenter = GetVec3(0, 0, 0);
                    pMesh.InitBBoxCenter.Set(pMesh.InitPositionMax);
                    pMesh.InitBBoxCenter.Add(pMesh.InitPositionMin);
                    pMesh.InitBBoxCenter.Mul(0.5);
                    pMesh.InitBBoxExtent = GetVec3(0, 0, 0);
                    pMesh.InitBBoxExtent.Set(pMesh.InitPositionMax);
                    pMesh.InitBBoxExtent.Negate(pMesh.InitPositionMin);
                    pMesh.InitBBoxExtent.Mul(0.5);
                    if (DEBUG_LOG) {
                        console.log(
                            `${path} BBOX Center: ${pMesh.InitBBoxCenter.x}, ${pMesh.InitBBoxCenter.y}, ${pMesh.InitBBoxCenter.z} Extent : ${pMesh.InitBBoxExtent.x}, ${pMesh.InitBBoxExtent.y}, ${pMesh.InitBBoxExtent.z}`,
                        );
                    }
                }

                if (preTransformMat) {
                    const tvec = GStack.TempVec3;
                    const resVec = GStack.TempVec3_2;
                    for (let i = 0; i < VertexBufferCPU.length; i += 3) {
                        tvec.Set3(VertexBufferCPU[i], VertexBufferCPU[i + 1], VertexBufferCPU[i + 2]);
                        tvec.Negate(pMesh.InitBBoxCenter);
                        Vec3MulMatrix(tvec, preTransformMat, resVec);
                        resVec.Add(pMesh.InitBBoxCenter);
                        VertexBufferCPU[i] = resVec.x;
                        VertexBufferCPU[i + 1] = resVec.y;
                        VertexBufferCPU[i + 2] = resVec.z;
                    }
                }

                //pMesh.VertexBufferGPU = gl.createBuffer();
                gl.bindBuffer(gl.ARRAY_BUFFER, pMesh.VertexBufferGPU);
                gl.bufferData(gl.ARRAY_BUFFER, VertexBufferCPU, gl.STATIC_DRAW);
                if (DEBUG_LOG_ATTRIBUTES) {
                    //console.log(`NUM VERTICES :` + this.NumVertices);
                    //const numElements = GLTF_ELEMENTS_PER_TYPE[curAccessor.type];
                    //this.DebugLogAttributeContents(`POSITIONS`, data, numElements);
                }
            }

            //Extract Vertices
            pMesh.InitBBoxExtentNorm = new Vector3(1, 1, 1);
            if (bGenerateSDFTexture) {
                const scale = GetVec3(1, 1, 1);
                if (pMesh.InitBBoxExtent) {
                    if (1 /* uniformScale */) {
                        pMesh.InitBBoxExtentNorm.Set(pMesh.InitBBoxExtent);
                        const maxExtent = Math.max(
                            pMesh.InitBBoxExtent.z,
                            Math.max(pMesh.InitBBoxExtent.x, pMesh.InitBBoxExtent.y),
                        );
                        scale.SetSame(1.0 / maxExtent);
                        pMesh.InitBBoxExtentNorm.Mul(1 / maxExtent);
                    } else {
                        const scaleIfNotZero = (p: number, div: number): number => {
                            if (div > 0.00001) {
                                return p / div;
                            } else {
                                return p;
                            }
                        };
                        scale.x = scaleIfNotZero(scale.x, pMesh.InitBBoxExtent.x);
                        scale.y = scaleIfNotZero(scale.y, pMesh.InitBBoxExtent.y);
                        scale.z = scaleIfNotZero(scale.z, pMesh.InitBBoxExtent.z);
                    }
                }

                /* if (pMesh.InitBBoxExtent) {
                    console.log(
                        `Mesh SDF bounds ${pMesh.InitBBoxExtent.x} ${pMesh.InitBBoxExtent.y} ${pMesh.InitBBoxExtent.z}`,
                    );
                } */
                //leave the border empty
                const voxelSize = 2 / GSDFMeshGenerator.kTextureSize;
                //const mesh3dscale = 1.0 - (voxelSize + voxelSize * 0.5);
                const mesh3dscale = 1.0 - voxelSize * 2.0;
                scale.Mul(mesh3dscale);
                //scale.Mul(0.9);
                const translate = GetVec3(0, 0, 0);
                if (pMesh.InitBBoxCenter) {
                    translate.Set(pMesh.InitBBoxCenter);
                }

                if (pMesh.NumIndices > 0 && IndexBufferCPU) {
                    // Allocate aligned storage for the texture
                    const numPixels = pMesh.NumIndices * 3;
                    const rowSize = 450;
                    const texWidth = numPixels < rowSize ? numPixels : rowSize;
                    const texHeight = Math.floor(numPixels / rowSize) + 1;
                    const numPixelsAligned = texWidth * texHeight;
                    pMesh.GeometryBufferCPU = new Float32Array(numPixelsAligned);
                    //pMesh.GeometryBufferCPU = new Float32Array(pMesh.NumIndices * 3);
                    /* console.log(
                        `${path} /n Creating Geometry Buffer: Index Buffer Size ${IndexBufferCPU.length}, VB Size ${VertexBufferCPU.length}, Geometry Buffer Size ${numPixels}, AlignedSize : ${pMesh.GeometryBufferCPU.length}`,
                    ); */
                    const curV = new Vector3(0, 0, 0);
                    for (let i = 0; i < pMesh.NumIndices; i++) {
                        const vi = IndexBufferCPU[i];

                        const readOffset = vi * 3;

                        curV.x = VertexBufferCPU[readOffset + 0];
                        curV.y = VertexBufferCPU[readOffset + 1];
                        curV.z = VertexBufferCPU[readOffset + 2];

                        curV.Negate(translate);
                        curV.Mul3(scale.x, scale.y, scale.z);

                        /* if (preTransformMat) {
                            const tvec = GStack.TempVec3;
                            tvec.Set(curV);
                            Vec3MulMatrix(tvec, preTransformMat, curV);
                        } */

                        const writeOffset = i * 3;

                        pMesh.GeometryBufferCPU[writeOffset + 0] = curV.x;
                        pMesh.GeometryBufferCPU[writeOffset + 1] = curV.y;
                        pMesh.GeometryBufferCPU[writeOffset + 2] = curV.z;
                    }
                } else {
                    pMesh.GeometryBufferCPU = new Float32Array(pMesh.NumVertices * 3);
                    for (let i = 0; i < pMesh.NumVertices; i++) {
                        const vOffset = i * 3;

                        const x = VertexBufferCPU[vOffset + 0];
                        const y = VertexBufferCPU[vOffset + 1];
                        const z = VertexBufferCPU[vOffset + 2];

                        pMesh.GeometryBufferCPU[vOffset + 0] = (x + translate.x) * scale.x;
                        pMesh.GeometryBufferCPU[vOffset + 1] = (y + translate.y) * scale.y;
                        pMesh.GeometryBufferCPU[vOffset + 2] = (z + translate.z) * scale.z;
                    }
                }

                pMesh.SDFTexture3D = GSDFMeshGenerator.GenerateFromGeometry(
                    gl,
                    pMesh.GeometryBufferCPU!,
                    pMesh.NumIndices > 0 ? pMesh.NumIndices : pMesh.NumVertices,
                    pMesh.InitBBoxExtentNorm,
                );
            } //GENERATE SDF

            //Get UVs
            {
                const data = await asset.LoadAccessor(curPrimitive.attributes.TEXCOORD_0);
                //pMesh.TexCoordsBufferGPU = gl.createBuffer();
                gl.bindBuffer(gl.ARRAY_BUFFER, pMesh.TexCoordsBufferGPU);
                gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
                if (DEBUG_LOG_ATTRIBUTES) {
                    const curAccessor = asset.gltf.accessors![curPrimitive.attributes.TEXCOORD_0];
                    const numElements = GLTF_ELEMENTS_PER_TYPE[curAccessor.type];
                    this.DebugLogAttributeContents(`TEXCOORDS`, data, numElements);
                }
            }

            //Get Normals
            {
                const data = await asset.LoadAccessor(curPrimitive.attributes.NORMAL);

                if (preTransformMat) {
                    const tvec = GStack.TempVec3;
                    const resVec = GStack.TempVec3_2;
                    for (let i = 0; i < data.length; i += 3) {
                        tvec.Set3(data[i], data[i + 1], data[i + 2]);
                        Vec3MulMatrix(tvec, preTransformMat, resVec);
                        data[i] = resVec.x;
                        data[i + 1] = resVec.y;
                        data[i + 2] = resVec.z;
                    }
                }

                //pMesh.NormalsBufferGPU = gl.createBuffer();
                gl.bindBuffer(gl.ARRAY_BUFFER, pMesh.NormalsBufferGPU);
                gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
                if (DEBUG_LOG_ATTRIBUTES) {
                    const curAccessor = asset.gltf.accessors![curPrimitive.attributes.NORMAL];
                    const numElements = GLTF_ELEMENTS_PER_TYPE[curAccessor.type];
                    this.DebugLogAttributeContents(`NORMALS`, data, numElements);
                }
            }

            //Get Tangents
            if (curPrimitive.attributes.TANGENT !== undefined) {
                const data = await asset.LoadAccessor(curPrimitive.attributes.TANGENT);

                if (preTransformMat) {
                    const tvec = GStack.TempVec3;
                    const resVec = GStack.TempVec3_2;
                    for (let i = 0; i < data.length; i += 4) {
                        tvec.Set3(data[i], data[i + 1], data[i + 2]);
                        Vec3MulMatrix(tvec, preTransformMat, resVec);
                        data[i] = resVec.x;
                        data[i + 1] = resVec.y;
                        data[i + 2] = resVec.z;
                    }
                }

                pMesh.TangentsBufferGPU = gl.createBuffer();
                gl.bindBuffer(gl.ARRAY_BUFFER, pMesh.TangentsBufferGPU);
                gl.bufferData(gl.ARRAY_BUFFER, data, gl.STATIC_DRAW);
                if (DEBUG_LOG_ATTRIBUTES) {
                    const curAccessor = asset.gltf.accessors![curPrimitive.attributes.TANGENT];
                    const numElements = GLTF_ELEMENTS_PER_TYPE[curAccessor.type];
                    this.DebugLogAttributeContents(`TANGENT`, data, numElements);
                }
            } else {
                //console.log(`GENERATING MISSING TANGENT ATTRIBUTE IN ${path}`);
                //Generate Tangents from Normals
                //const tangents: number[] = [];
                const data = await asset.LoadAccessor(curPrimitive.attributes.NORMAL);
                const nStride = 3;
                const tStride = 4;
                const numVerts = data.length / nStride;
                const tangents = new Float32Array(numVerts * tStride);
                //const normalsArr: { x: number; y: number; z: number }[] = [];
                for (let i = 0; i < numVerts; i += 1) {
                    //normalsArr.push({ x: data[i], y: data[i + 1], z: data[i + 2] });
                    const nOffset = i * nStride;
                    const n = GStack.TempVec3;
                    n.Set3(data[nOffset], data[nOffset + 1], data[nOffset + 2]);
                    const up = GStack.TempVec3_2;
                    up.Set3(0.0, 1.0, 0.0);
                    if (Math.abs(n.y) > 0.999) {
                        up.Set3(1.0, 0.0, 0.0);
                    }
                    const tan = GStack.TempVec3_3;
                    tan.Set(n);
                    tan.Cross(up);
                    tan.Normalize();
                    /* tangents[i] = tan.x;
                    tangents[i + 1] = tan.y;
                    tangents[i + 2] = tan.z; */
                    const tOffset = i * tStride;
                    tangents[tOffset] = tan.x;
                    tangents[tOffset + 1] = tan.y;
                    tangents[tOffset + 2] = tan.z;
                    tangents[tOffset + 3] = 1.0;
                }
                pMesh.TangentsBufferGPU = gl.createBuffer();
                gl.bindBuffer(gl.ARRAY_BUFFER, pMesh.TangentsBufferGPU);
                gl.bufferData(gl.ARRAY_BUFFER, tangents, gl.STATIC_DRAW);
            }

            pMesh.GenerateVAO(gl);

            pMesh.bCanDraw = true;
        } catch (e) {
            console.error(e);
            console.log(path);
        }
    }

    static DebugLogAttributeContents(
        name: string,
        data: Float32Array | Uint8Array | Int8Array | Int16Array | Uint16Array | Uint32Array,
        numElements: number,
    ) {
        if (data instanceof Float32Array) {
            console.log(name);
            if (numElements == 3) {
                const vecArr: { x: number; y: number; z: number }[] = [];
                for (let i = 0; i < data.length; i += numElements) {
                    vecArr.push({ x: data[i], y: data[i + 1], z: data[i + 2] });
                }
                console.log(vecArr);
            } else if (numElements == 2) {
                const vecArr: { x: number; y: number }[] = [];
                for (let i = 0; i < data.length; i += numElements) {
                    vecArr.push({ x: data[i], y: data[i + 1] });
                }
                console.log(vecArr);
            } else if (numElements == 4) {
                const vecArr: { x: number; y: number; z: number; w: number }[] = [];
                for (let i = 0; i < data.length; i += numElements) {
                    vecArr.push({ x: data[i], y: data[i + 1], z: data[i + 2], w: data[i + 3] });
                }
                console.log(vecArr);
            }
        }
    }
}
